import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Typography, useTheme } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import Editor from '@monaco-editor/react'
import { toast } from 'react-toastify'

import { Icons } from '../../utils/utilities'

const navigationKeys = ["ArrowUp", "ArrowDown", "ArrowRight", "ArrowLeft"]

const QuestionsPreviewDialog = ( {
    setReadOnlyModeForFileView = () => { },
    setSelectedSection = () => { },
    readOnly = false,
    file,
    setFile = () => { },
    setQuestionFiles,
    section,
    questionsPreview,
    setQuestionsPreview,
    setIsImporting,
    isImporting,
    fileRawContent // Ensure you pass the raw string content of the file here
} ) => {
    const { palette, border } = useTheme()
    const monacoRef = useRef( null )
    const editorRef = useRef( null )

    // 1. Setup Custom Syntax Highlighting (Monarch)
    const handleEditorWillMount = ( monaco ) => {
        monaco.languages.register( { id: 'aiken-multilingual' } )

        monaco.languages.setMonarchTokensProvider( 'aiken-multilingual', {
            tokenizer: {
                root: [
                    [/:: QUESTION_(START|END) ::/, 'custom-delimiter'],
                    [/\[[A-Za-z]{2}\]/, 'custom-lang-tag'],
                    [/ANSWER: .*/, 'custom-answer'],
                    [/[A-Z][.)] .*/, 'custom-option'],
                ]
            }
        } )

        monaco.editor.defineTheme( 'aikenTheme', {
            base: 'vs',
            inherit: true,
            rules: [
                { token: 'custom-delimiter', foreground: '979797', fontStyle: 'bold' },
                { token: 'custom-lang-tag', foreground: '4EC9B0', fontStyle: 'bold' },
                { token: 'custom-answer', foreground: '000000', fontStyle: 'bold' },
                { token: 'custom-option', foreground: '888888' },
            ],
            colors: { 'editor.background': '#fefefe' }
        } )
    }

    const onEditorMount = ( editor, monaco ) => {
        editorRef.current = editor
        monacoRef.current = monaco

        editor.onKeyDown( ( e ) => {
            // Allow navigation/selection keys, but block content modification

            const isNavigation = navigationKeys.includes( e.code )
            const isCopy = ( e.ctrlKey || e.metaKey ) && e.keyCode === "KeyC" // Ctrl+C

            if ( !isNavigation && !isCopy ) {
                e.preventDefault()
                e.stopPropagation()
            }
        } )

        // Immediate execution on mount
        // Force a layout recalculation after the Dialog animation finishes
        setTimeout( () => {
            editor.layout()
            applyMarkers( monaco, editor, questionsPreview?.errors )
        }, 200 )

        // Optional: Reveal the first error line automatically
        // const errorKeys = Object.keys( questionsPreview?.errors || {} )
        // if ( errorKeys.length > 0 ) {
        //     const firstErrorLine = questionsPreview.errors[errorKeys[0]].line
        //     editor.revealLineInCenter( firstErrorLine )
        // }
    }

    const applyMarkers = async ( monaco, editor, errors ) => {
        if ( !monaco || !editor || !errors ) return

        // Get the current model directly from the editor instance
        const model = editor.getModel()
        if ( !model ) return

        const markers = Object.values( errors ).map( err => ( {
            startLineNumber: Number( err.line ), // Ensure it's a number
            endLineNumber: Number( err.line ),
            startColumn: 1,
            endColumn: model.getLineMaxColumn( Number( err.line ) ), // More precise than 1000
            message: err.messages.join( '\n' ),
            severity: monaco.MarkerSeverity.Error,
        } ) )


        // Use a unique owner string, or 'javascript' / 'aiken-multilingual'
        // Sometimes 'owner' is ignored if the editor thinks a language service should own it
        const owner = model.id

        console.log( "Setting markers for owner:", owner, markers )

        // Set markers directly
        await monaco.editor.setModelMarkers( model, owner, markers )
    }

    // Keep the useEffect for when questionsPreview data changes while open
    useEffect( () => {
        if ( monacoRef.current && editorRef.current ) {
            applyMarkers( monacoRef.current, editorRef.current, questionsPreview?.errors )
        }
    }, [questionsPreview] )

    const importQuestions = async () => {
        try {
            setIsImporting( true )
            setQuestionFiles( prev => [...prev, {
                id: Date.now(),
                section,
                file,
                questionCount: questionsPreview?.questions.length
            }] )
            handleClose()
        } catch ( err ) {
            toast.error( "Something went wrong while importing questions", { containerId: "main" } )
        } finally {
            setIsImporting( false )
        }
    }

    const handleClose = () => {
        setFile( null )
        setQuestionsPreview( false )
        setSelectedSection( null )
        setReadOnlyModeForFileView( false )
    }

    return (
        <Dialog onClose={handleClose} open={!!questionsPreview} PaperProps={{ sx: { minWidth: "80vw" } }}>
            <DialogTitle sx={{ fontSize: "18px", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Imported file preview
                {questionsPreview?.status === "loaded" && (
                    <Typography component="span" color={Object.keys( questionsPreview.errors ).length > 0 ? "error" : "success.main"} variant="subtitle2">
                        {questionsPreview.questions.length} Questions Detected | {Object.keys( questionsPreview.errors ).length} Errors Found
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent sx={{ padding: "0" }}>
                {questionsPreview?.status === "loaded" && (
                    <Box border={border[1]} borderRadius="5px" overflow="hidden">
                        <Editor
                            height="60vh"
                            defaultLanguage="aiken-multilingual"
                            theme="aikenTheme"
                            value={fileRawContent || ( Array.isArray( questionsPreview.data ) ? questionsPreview.data.join( '\n' ) : typeof questionsPreview.data === 'string' ? questionsPreview.data : "" )}
                            beforeMount={handleEditorWillMount}
                            onMount={onEditorMount}
                            options={{
                                // readOnly: true,
                                fontSize: 14,
                                minimap: { enabled: false },
                                scrollBeyondLastLine: false,
                                lineNumbers: 'on',
                                glyphMargin: true, // Crucial for error visibility
                                lineDecorationsWidth: 10,
                                folding: false,
                                fixedOverflowWidgets: true, // Ensures error tooltips aren't cut off by the Dialog
                            }}
                        />
                    </Box>
                )}

                {questionsPreview?.status === "loading" && (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="400px" bgcolor="#1e1e1e">
                        <CircularProgress sx={{ color: "white", mb: 2 }} />
                        <Typography color="white">Extracting questions and validating translations...</Typography>
                    </Box>
                )}

                <Box borderTop={border[1]} bgcolor={palette.greyedOut} padding="10px 20px" gap="20px" alignItems="center" display="flex">
                    {questionsPreview && !readOnly && <Button disableElevation disabled={Object.keys( questionsPreview?.errors || {} ).length > 0 || isImporting || !questionsPreview?.questions || questionsPreview?.questions?.length === 0} startIcon={isImporting ? <CircularProgress size={14} /> : Icons.default.DownloadForOfflineIcon} sx={{ textTransform: "capitalize" }} onClick={async () => { importQuestions( questionsPreview.questions ) }} variant="contained">{isImporting ? "Importing questions..." : "Import"}</Button>}
                    <Button disableElevation sx={{ textTransform: "capitalize" }} onClick={handleClose} color='error' variant="contained">Close</Button>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default QuestionsPreviewDialog

/*


        monaco.languages.registerInlayHintsProvider( 'aiken-multilingual', {
            provideInlayHints: ( model, range ) => {
                const hints = []
                const lines = model.getLinesContent()

                lines.forEach( ( content, i ) => {
                    const lineNum = i + 1
                    // Look for tags like [EN], [HI], etc.
                    const match = content.match( /\[([A-Za-z]{2})\]/ )
                    if ( match ) {
                        const tag = match[1].toLowerCase()
                        const label = by639_1[tag]?.name || 'Unknown Language'

                        hints.push( {
                            // Position the hint right after the closing bracket ']'
                            position: { lineNumber: lineNum, column: content.length + 1 },
                            label: `: ${label}`, // The text to display
                            kind: monaco.languages.InlayHintKind.Parameter,
                            paddingLeft: true
                        } )
                    }
                } )
                return { hints, dispose: () => { } }
            }
        } )
*/